import { Button, Card, useToast } from '@citizen-walk/shared-ui';
import type {
  AiAggregateScoresDef,
  AiAssessmentDef,
  AiClassificationAxis,
  AiOutputFieldDef,
  AiOutputFormat,
  AiOutputFormatSchema,
  StreetSurveyDisplayDef,
  AiOutputObjectArrayFieldDef,
  DistrictMode,
  PostDetailVisibility,
} from '@citizen-walk/shared-types';
import { normalizeAiOutputFormat, POST_DETAIL_VISIBILITY_KEYS } from '@citizen-walk/shared-types';
import { useMemo, useState } from 'react';
import {
  archiveMaster,
  upsertMaster,
  useAiAnalysisSettings,
  useAiOutputFormats,
} from './useMasters';
import { MasterHistoryView } from './MasterHistoryView';
import { MasterImportExportButtons, RowExportButton } from './MasterImportExportButtons';
import { stripSystemFields } from './jsonImportExport';
import { ModeSelectField } from './components/ModeSelectField';
// 使用状況の pill バッジ (AI 解析テンプレ M1 一覧と共用)。
import { UsageBadges, type UsageBadgeItem } from './components/UsageBadges';
// 別名で保存 (複製) / ドキュメント ID の検証は M1 統合エディタと同じ純関数を再利用する。
import {
  duplicateDraft,
  groupOutputFormatUsage,
  uniqueSlug,
  validateDocumentId,
  type OutputFormatUsage,
} from './aiAnalysisEditorHelpers';
// 使用状況の集計に地区一覧が要る (地区は outputFormatIdOverride で M3 を直接指定できる)。
import { useDistricts } from '../districts-admin';
// Sprint AI統合エディタ — fields[] 編集部品を共通モジュールへ切り出し (AiAnalysisEditorPage と共有)。
import {
  FieldEditor,
  cleanField,
  cloneField,
  emptyChildField,
  emptyFieldOfKind,
  INPUT_CLASS,
  type ChildFieldKind,
  type OutputFieldKind,
} from './aiOutputFieldsEditor';

/**
 * M3 aiOutputFormats 管理画面 (docs/04 §3-1 M3、Phase 5 admin 拡張 / Sprint M3)。
 *
 * R-32 step 2 で schema が `unknown` → `AiOutputFormatSchema` に正式型化されたのを受け、
 * SimpleJsonMasterPage の生 JSON 編集を専用フォームに置換。
 *
 * 構造化 form の利点:
 *  - JSON 構文ミス排除
 *  - classificationAxes / assessment.levels の行追加・削除を直感的 UI で操作
 *  - primary 軸が複数 / 0 件などの shape 不正を save 時にブロック
 *  - customSchemaText のみ自由記述 (admin の自動生成 override 用途を残す)
 *
 * 旧 AiCategoryMastersPage の rows[] 編集パターンを classificationAxes / levels に流用。
 */

const COLLECTION_NAME = 'aiOutputFormats';

// INPUT_CLASS は aiOutputFieldsEditor から import (fields[] 編集部品と共有)。

const FIELD_CLASS =
  'w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-400';

/** 行 (classificationAxis) の編集中表現。role は select の都合で '' を許容。 */
interface AxisDraft {
  codeField: string;
  nameField: string;
  subField: string;
  label: string;
  role: '' | 'primary' | 'secondary';
}

interface FormDraft {
  id: string;
  displayName: string;
  mode: DistrictMode;
  rootKey: string;
  classificationAxes: AxisDraft[];
  assessmentPositiveLabel: string;
  assessmentNegativeLabel: string;
  assessmentLevels: string[];
  aggregatePositiveKey: string;
  aggregateNegativeKey: string;
  aggregateOverallKey: string;
  reasoningField: string;
  customSchemaText: string;
  /**
   * 列名動的化レベル 3 (docs/105) の出力フィールド定義。draft 内では AiOutputFieldDef[] を
   * そのまま保持する。空配列なら save 時に schema.fields は省略され、設定済みなら
   * classificationAxes より優先される (列名の唯一の真実)。
   */
  fields: AiOutputFieldDef[];
  /**
   * issue #24: このフォーマットの投稿詳細表示で各要素を表示するかの **テンプレ既定**。
   * sparse な partial で保持 (キー未指定 = 表示)。地区側 `aiOutputDisplayOverrides` がキー単位で上書きする。
   */
  postDetailVisibility: PostDetailVisibility;
  /**
   * docs/27 §9: 以前この画面で編集していた机上調査の設定 (schema.streetSurvey)。編集欄は撤去したが、
   * 保存し直しても消えないよう、そのまま持ち回る (docs/27 §17-4)。
   */
  streetSurvey: StreetSurveyDisplayDef;
  /** docs/27 §15-3: 机上調査の解析の設定（新方式）のテンプレ既定 (地区で項目ごとに上書き可)。 */
}

// OutputFieldKind / ChildFieldKind は aiOutputFieldsEditor から import。

const EMPTY_AXIS: AxisDraft = {
  codeField: '',
  nameField: '',
  subField: '',
  label: '',
  role: '',
};

const EMPTY_DRAFT: FormDraft = {
  id: '',
  displayName: '',
  mode: 'ecw',
  rootKey: '',
  classificationAxes: [],
  assessmentPositiveLabel: '',
  assessmentNegativeLabel: '',
  assessmentLevels: [],
  aggregatePositiveKey: '',
  aggregateNegativeKey: '',
  aggregateOverallKey: '',
  reasoningField: '',
  customSchemaText: '',
  fields: [],
  postDetailVisibility: {},
  streetSurvey: {},
};

// emptyFieldOfKind / emptyChildField は aiOutputFieldsEditor から import。

/** issue #24: 投稿詳細表示トグルの日本語ラベル (POST_DETAIL_VISIBILITY_KEYS と同順)。 */
const POST_DETAIL_VISIBILITY_LABELS: Record<keyof PostDetailVisibility, string> = {
  classificationAxes: '分類軸（カテゴリ/ハザード等の code+name 列）',
  assessment: '評価ラベル（ポジ/ネガ + レベル + 行スコア）',
  aggregateScores: '集約スコア（ハッと/ホッと・いいね/もったいない 等のサマリ）',
  fieldsTable: 'テーブル状の出力（fields[] 駆動の動的列テーブル）',
};

function axisFromSchema(a: AiClassificationAxis): AxisDraft {
  return {
    codeField: a.codeField,
    nameField: a.nameField,
    subField: a.subField ?? '',
    label: a.label,
    // AxisDraft.role は primary / secondary のみ (M3 ページの select 都合)。
    // shared-types で tertiary が追加されたため、tertiary / 未指定は '' に畳む。
    role: a.role === 'primary' || a.role === 'secondary' ? a.role : '',
  };
}

function draftFromItem(item: AiOutputFormat & { id: string }): FormDraft {
  const schema: AiOutputFormatSchema = item.schema;
  return {
    id: item.id,
    displayName: item.displayName,
    mode: item.mode,
    rootKey: schema.rootKey ?? '',
    classificationAxes: schema.classificationAxes.map(axisFromSchema),
    assessmentPositiveLabel: schema.assessment.positiveLabel,
    assessmentNegativeLabel: schema.assessment.negativeLabel,
    assessmentLevels: [...schema.assessment.levels],
    aggregatePositiveKey: schema.aggregateScores.positiveKey ?? '',
    aggregateNegativeKey: schema.aggregateScores.negativeKey ?? '',
    aggregateOverallKey: schema.aggregateScores.overallKey ?? '',
    reasoningField: schema.reasoningField,
    customSchemaText: schema.customSchemaText ?? '',
    fields: schema.fields ? schema.fields.map(cloneField) : [],
    postDetailVisibility: { ...(schema.postDetailVisibility ?? {}) },
    streetSurvey: { ...(schema.streetSurvey ?? {}) },
  };
}

// cloneField / cleanField / cleanSource / cleanReasonField は aiOutputFieldsEditor から import。

function validateAiOutputFormat(raw: unknown): (AiOutputFormat & { id: string }) | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.id !== 'string' || !r.id.trim()) return null;
  if (typeof r.displayName !== 'string') return null;
  if (typeof r.mode !== 'string') return null;
  if (!r.schema || typeof r.schema !== 'object') return null;
  return r as unknown as AiOutputFormat & { id: string };
}

export function AiOutputFormatsPage() {
  // ドキュメント ID の重複判定 (アーカイブ済との衝突も検出) のためアーカイブ済も購読する。
  // 一覧に出すのは従来どおり有効な行のみ (activeItems)。
  const state = useAiOutputFormats({ includeArchived: true });
  // 使用状況 (経路 1): AI 解析テンプレ (M1) の outputFormatId。アーカイブ済テンプレからの
  // 参照も「使用中」として拾う (未使用と誤判定してアーカイブしないため)。
  const m1State = useAiAnalysisSettings({ includeArchived: true });
  // 使用状況 (経路 2): 地区の aiAnalysis.outputFormatIdOverride (直接指定)。
  const districtsState = useDistricts();
  const { toast } = useToast();
  const [editing, setEditing] = useState<FormDraft | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [historyTarget, setHistoryTarget] = useState<{ id: string; name: string } | null>(null);
  /**
   * 「別名で保存」直後 (未保存の複製) の複製元フォーマット表示名 (null=複製状態でない)。
   * 複製注記の表示条件に使う。保存 / 別行の編集 / 新規作成でクリアする。
   */
  const [duplicatedFrom, setDuplicatedFrom] = useState<string | null>(null);
  /**
   * 新規作成中のドキュメント ID を管理者が手で編集したか。
   * true の間は表示名を変えても ID を自動生成し直さない (手入力を上書きしないため)。
   */
  const [idManuallyEdited, setIdManuallyEdited] = useState(false);
  /**
   * ドキュメント ID の入力欄に一度でも触れたか (入力 / フォーカスを外す / 保存を試みる)。
   * 触る前の空欄に「未入力」の赤字を出すと開いた瞬間から画面が壊れて見えるため、
   * 未入力エラーの表示条件に使う (不正文字・重複は触った結果なので即時表示する)。
   */
  const [idTouched, setIdTouched] = useState(false);

  /** 全フォーマット (アーカイブ済込み)。ID 重複判定と取込時の既存判定に使う。 */
  const allItems = useMemo(() => (state.status === 'ready' ? state.items : []), [state]);
  /** 有効フォーマット (一覧表示・書き出し対象)。 */
  const activeItems = useMemo(() => allItems.filter((it) => !it.archivedAt), [allItems]);
  const archivedCount = allItems.length - activeItems.length;
  /**
   * 新規 id (slug) の衝突判定に使う既存 id 集合。
   * **アーカイブ済も必ず含める** (新規保存は merge:false の全置換なので、アーカイブ済と
   * 同 id で保存すると復元不能な上書きになる)。
   */
  const existingIds = useMemo(() => allItems.map((it) => it.id), [allItems]);

  /** フォーマット id → 使用状況 (使用テンプレート / 使用地区、2 経路を集計)。 */
  const usageByFormat = useMemo(
    () =>
      groupOutputFormatUsage({
        templates: m1State.status === 'ready' ? m1State.items : [],
        districts: districtsState.status === 'ready' ? districtsState.items : [],
      }),
    [m1State, districtsState],
  );
  /** 使用状況の読込が終わったか (未完了のうちは「未使用」と断定しない)。 */
  const usageReady = m1State.status === 'ready' && districtsState.status === 'ready';

  async function handleImport(importedItems: (AiOutputFormat & { id: string })[]) {
    for (const it of importedItems) {
      const existing = allItems.find((x) => x.id === it.id);
      // Sprint BG-MasterIO follow-up — system field を除去
      const { id, ...rest } = stripSystemFields(it);
      await upsertMaster<AiOutputFormat>({
        collectionName: COLLECTION_NAME,
        id: id as string,
        uid: 'admin-stub',
        isNew: !existing,
        data: rest as Omit<
          AiOutputFormat,
          'id' | 'createdAt' | 'updatedAt' | 'createdBy' | 'updatedBy'
        >,
      });
    }
  }

  function handleNew() {
    setEditing({ ...EMPTY_DRAFT, classificationAxes: [], assessmentLevels: [] });
    setIsNew(true);
    setDuplicatedFrom(null);
    setIdManuallyEdited(false);
    setIdTouched(false);
  }

  function handleEdit(item: AiOutputFormat & { id: string }) {
    setEditing(draftFromItem(item));
    setIsNew(false);
    setDuplicatedFrom(null);
    setIdManuallyEdited(false);
    setIdTouched(false);
  }

  function handleCancel() {
    setEditing(null);
    setIsNew(false);
    setDuplicatedFrom(null);
    setIdManuallyEdited(false);
    setIdTouched(false);
  }

  /**
   * 別名保存 (複製) の共通処理: 渡された draft の内容を丸ごと引き継いだ新規フォーマットの
   * 下書きを用意する。**この時点では保存しない** (誤操作で意図しないフォーマットが増えないよう、
   * 確定は「保存」ボタンに委ねる)。schema は deep copy されるので、複製後の編集は元に影響しない。
   */
  function applyDuplicate(source: FormDraft) {
    setEditing(duplicateDraft(source, existingIds));
    setIsNew(true);
    setDuplicatedFrom(source.displayName);
    setIdManuallyEdited(false); // 複製直後は `{元 id}-copy`。手入力すればそこで固定される
    setIdTouched(false);
    toast(
      'success',
      `「${source.displayName}」の内容を複製しました。表示名称とドキュメント ID を確認して「保存」を押してください。`,
    );
  }

  /** 編集中のフォーマットを別名で保存する (未保存の編集内容も含めて引き継ぐ)。 */
  function handleDuplicateCurrent() {
    if (!editing) return;
    applyDuplicate(editing);
  }

  /** 一覧の行から複製する (その行を読み込んだうえで別名保存と同じ状態にする)。 */
  function handleDuplicateFromRow(item: AiOutputFormat & { id: string }) {
    applyDuplicate(draftFromItem(item));
  }

  /**
   * 表示名変更: 新規時かつ ID 未編集なら id を slug 自動生成する。
   * 管理者が ID を手入力済み (idManuallyEdited) のときは、その ID を上書きしない。
   */
  function setDisplayName(name: string) {
    if (!editing) return;
    if (isNew && !idManuallyEdited) {
      setEditing({ ...editing, displayName: name, id: uniqueSlug(name, existingIds) });
    } else {
      setEditing({ ...editing, displayName: name });
    }
  }

  /** ドキュメント ID の手入力 (新規時のみ)。以後は表示名変更で自動生成し直さない。 */
  function setDocumentId(id: string) {
    if (!editing) return;
    setIdManuallyEdited(true);
    setIdTouched(true);
    setEditing({ ...editing, id });
  }

  /** ドキュメント ID を表示名からの自動生成に戻す。 */
  function regenerateDocumentId() {
    if (!editing) return;
    setIdManuallyEdited(false);
    setEditing({ ...editing, id: uniqueSlug(editing.displayName, existingIds) });
  }

  /**
   * 新規作成中のドキュメント ID の問題 (未入力 / 使用不可文字 / 既存重複)。
   * 保存の可否判定に使う (最終防御は handleSave 側)。
   */
  const idError = useMemo(
    () =>
      editing && isNew ? validateDocumentId(editing.id, existingIds, '出力フォーマット') : null,
    [editing, isNew, existingIds],
  );

  /**
   * 入力欄の下に赤字で出すエラー。
   *  - 使用できない文字 / ハイフンの位置 / 既存との重複 = 入力した結果なので即時表示する。
   *  - 未入力 = 一度も触っていない間は出さない (開いた直後から赤字だと壊れて見えるため)。
   */
  const visibleIdError = useMemo(() => {
    if (idError == null) return null;
    if (!idTouched && !(editing?.id ?? '').trim()) return null;
    return idError;
  }, [idError, idTouched, editing]);

  function addAxis() {
    if (!editing) return;
    setEditing({
      ...editing,
      classificationAxes: [...editing.classificationAxes, { ...EMPTY_AXIS }],
    });
  }

  function updateAxis(index: number, patch: Partial<AxisDraft>) {
    if (!editing) return;
    const target = editing.classificationAxes[index];
    if (!target) return;
    const next = [...editing.classificationAxes];
    next[index] = { ...target, ...patch };
    setEditing({ ...editing, classificationAxes: next });
  }

  function removeAxis(index: number) {
    if (!editing) return;
    setEditing({
      ...editing,
      classificationAxes: editing.classificationAxes.filter((_, i) => i !== index),
    });
  }

  function addLevel() {
    if (!editing) return;
    setEditing({ ...editing, assessmentLevels: [...editing.assessmentLevels, ''] });
  }

  function updateLevel(index: number, value: string) {
    if (!editing) return;
    if (editing.assessmentLevels[index] === undefined) return;
    const next = [...editing.assessmentLevels];
    next[index] = value;
    setEditing({ ...editing, assessmentLevels: next });
  }

  function removeLevel(index: number) {
    if (!editing) return;
    setEditing({
      ...editing,
      assessmentLevels: editing.assessmentLevels.filter((_, i) => i !== index),
    });
  }

  /* ===== 7. fields[] (列名動的化レベル 3) 編集ハンドラ ===== */

  const [newFieldKind, setNewFieldKind] = useState<OutputFieldKind>('text');

  function setFields(fields: AiOutputFieldDef[]) {
    if (!editing) return;
    setEditing({ ...editing, fields });
  }

  function addField() {
    if (!editing) return;
    setFields([...editing.fields, emptyFieldOfKind(newFieldKind)]);
  }

  function updateField(index: number, next: AiOutputFieldDef) {
    if (!editing) return;
    const arr = [...editing.fields];
    if (!arr[index]) return;
    arr[index] = next;
    setFields(arr);
  }

  function removeField(index: number) {
    if (!editing) return;
    setFields(editing.fields.filter((_, i) => i !== index));
  }

  function moveField(index: number, dir: -1 | 1) {
    if (!editing) return;
    const target = index + dir;
    if (target < 0 || target >= editing.fields.length) return;
    const arr = [...editing.fields];
    const tmp = arr[index]!;
    arr[index] = arr[target]!;
    arr[target] = tmp;
    setFields(arr);
  }

  /** 構造化設定 (classificationAxes 等) から fields[] を生成する橋渡し。 */
  function generateFieldsFromStructured() {
    if (!editing) return;
    // 現在の draft を一時 schema に変換 (validate せず最小組み立て)。
    const tmpAxes: AiClassificationAxis[] = editing.classificationAxes
      .filter((a) => a.codeField.trim() && a.nameField.trim())
      .map((a) => {
        const axis: AiClassificationAxis = {
          codeField: a.codeField.trim(),
          nameField: a.nameField.trim(),
          label: a.label.trim() || a.codeField.trim(),
        };
        const sub = a.subField.trim();
        if (sub) axis.subField = sub;
        if (a.role === 'primary' || a.role === 'secondary') axis.role = a.role;
        return axis;
      });
    const tmpSchema: AiOutputFormatSchema = {
      classificationAxes: tmpAxes,
      assessment: {
        positiveLabel: editing.assessmentPositiveLabel.trim(),
        negativeLabel: editing.assessmentNegativeLabel.trim(),
        levels: editing.assessmentLevels.map((l) => l.trim()).filter((l) => l.length > 0),
      },
      aggregateScores: {},
      reasoningField: editing.reasoningField.trim(),
    };
    const normalized = normalizeAiOutputFormat(tmpSchema);
    setFields((normalized.fields ?? []).map(cloneField));
    toast('success', '構造化設定から fields[] を生成しました');
  }

  function clearFields() {
    setFields([]);
  }

  /** object-array の子フィールド操作 */
  function addChildField(fieldIndex: number, kind: ChildFieldKind) {
    if (!editing) return;
    const f = editing.fields[fieldIndex];
    if (!f || f.kind !== 'object-array') return;
    const next: AiOutputObjectArrayFieldDef = {
      ...f,
      itemFields: [...(f.itemFields ?? []), emptyChildField(kind)],
    };
    updateField(fieldIndex, next);
  }

  function updateChildField(fieldIndex: number, childIndex: number, child: AiOutputFieldDef) {
    if (!editing) return;
    const f = editing.fields[fieldIndex];
    if (!f || f.kind !== 'object-array') return;
    const children = [...(f.itemFields ?? [])];
    if (!children[childIndex]) return;
    children[childIndex] = child;
    updateField(fieldIndex, { ...f, itemFields: children });
  }

  function removeChildField(fieldIndex: number, childIndex: number) {
    if (!editing) return;
    const f = editing.fields[fieldIndex];
    if (!f || f.kind !== 'object-array') return;
    updateField(fieldIndex, {
      ...f,
      itemFields: (f.itemFields ?? []).filter((_, i) => i !== childIndex),
    });
  }

  async function handleSave() {
    if (!editing) return;
    if (!editing.displayName.trim()) {
      toast('error', '表示名称 (displayName) は必須です');
      return;
    }
    if (isNew) {
      // 最終防御 (入力欄の赤字表示と同じ判定)。アーカイブ済との重複もここで弾く。
      setIdTouched(true);
      const idErr = validateDocumentId(editing.id, existingIds, '出力フォーマット');
      if (idErr) {
        toast('error', idErr);
        return;
      }
    }

    // fields[] (列名動的化レベル3、docs/105) が「列名の唯一の真実」のとき、構造化フォーム
    // (classificationAxes / primary / assessment.levels / reasoningField) の必須チェックを緩和する。
    // ECW フラット等、classifications を持たないフォーマットを fields[] のみで保存可能にするため。
    const usingFields = editing.fields.length > 0;

    // classificationAxes バリデーション (fields[] 未使用時のみ必須)
    if (!usingFields && editing.classificationAxes.length === 0) {
      toast('error', 'classificationAxes は最低 1 件必要です (または fields[] を定義してください)');
      return;
    }
    const cleanedAxes: AiClassificationAxis[] = [];
    let primaryCount = 0;
    for (let i = 0; i < editing.classificationAxes.length; i += 1) {
      const a = editing.classificationAxes[i]!;
      const codeField = a.codeField.trim();
      const nameField = a.nameField.trim();
      const label = a.label.trim();
      if (!codeField || !nameField || !label) {
        toast('error', `classificationAxes 行 ${i + 1}: codeField / nameField / label は必須です`);
        return;
      }
      const axis: AiClassificationAxis = { codeField, nameField, label };
      const subField = a.subField.trim();
      if (subField) axis.subField = subField;
      if (a.role === 'primary' || a.role === 'secondary') {
        axis.role = a.role;
        if (a.role === 'primary') primaryCount += 1;
      }
      cleanedAxes.push(axis);
    }
    if (!usingFields && primaryCount === 0) {
      toast('error', 'primary 軸を 1 件指定してください');
      return;
    }
    if (primaryCount > 1) {
      toast('error', `primary 軸は 1 件のみ許容されます (現在 ${primaryCount} 件)`);
      return;
    }

    // assessment.levels バリデーション
    const cleanedLevels = editing.assessmentLevels.map((l) => l.trim()).filter((l) => l.length > 0);
    if (!usingFields && cleanedLevels.length === 0) {
      toast('error', 'assessment.levels は最低 1 件必要です');
      return;
    }

    if (!usingFields && !editing.reasoningField.trim()) {
      toast('error', 'reasoningField は必須です');
      return;
    }

    // fields[] バリデーション (使用時のみ): key 必須 + 重複不可 (object-array の子フィールドも対象)。
    let cleanedFields: AiOutputFieldDef[] | null = null;
    if (editing.fields.length > 0) {
      const topKeys = new Set<string>();
      for (let i = 0; i < editing.fields.length; i += 1) {
        const f = editing.fields[i]!;
        const key = f.key.trim();
        if (!key) {
          toast('error', `出力フィールド 行 ${i + 1}: key は必須です`);
          return;
        }
        if (topKeys.has(key)) {
          toast('error', `出力フィールド 行 ${i + 1}: key "${key}" が重複しています`);
          return;
        }
        topKeys.add(key);
        // object-array の子フィールド key 検証 (key 入力済みの行のみ)。
        if (f.kind === 'object-array') {
          const childKeys = new Set<string>();
          const children = f.itemFields ?? [];
          for (let j = 0; j < children.length; j += 1) {
            const c = children[j]!;
            const ck = c.key.trim();
            if (!ck) {
              toast('error', `出力フィールド 行 ${i + 1} の子フィールド ${j + 1}: key は必須です`);
              return;
            }
            if (childKeys.has(ck)) {
              toast(
                'error',
                `出力フィールド 行 ${i + 1} の子フィールド ${j + 1}: key "${ck}" が重複しています`,
              );
              return;
            }
            childKeys.add(ck);
          }
        }
      }
      cleanedFields = editing.fields.map(cleanField);
    }

    const assessment: AiAssessmentDef = {
      positiveLabel: editing.assessmentPositiveLabel.trim(),
      negativeLabel: editing.assessmentNegativeLabel.trim(),
      levels: cleanedLevels,
    };

    const aggregateScores: AiAggregateScoresDef = {};
    if (editing.aggregatePositiveKey.trim()) {
      aggregateScores.positiveKey = editing.aggregatePositiveKey.trim();
    }
    if (editing.aggregateNegativeKey.trim()) {
      aggregateScores.negativeKey = editing.aggregateNegativeKey.trim();
    }
    if (editing.aggregateOverallKey.trim()) {
      aggregateScores.overallKey = editing.aggregateOverallKey.trim();
    }

    const schema: AiOutputFormatSchema = {
      classificationAxes: cleanedAxes,
      assessment,
      aggregateScores,
      reasoningField: editing.reasoningField.trim(),
    };
    if (editing.rootKey.trim()) schema.rootKey = editing.rootKey.trim();
    if (editing.customSchemaText.trim()) schema.customSchemaText = editing.customSchemaText;
    // fields[] が 1 件以上のときのみ書き込む (空配列で既存 format を汚さない)。
    if (cleanedFields && cleanedFields.length > 0) schema.fields = cleanedFields;
    // issue #24: 投稿詳細表示の既定。upsertMaster は setDoc(merge:true) で schema を deep-merge する
    // ため、sparse (false キーのみ) 書込だと「非表示→表示」に戻したとき旧 false が map に残り再チェック
    // 不能になる。全 4 キーを明示 boolean で書いて確実に上書きする (checked=true も含む)。
    const pv: PostDetailVisibility = {
      classificationAxes: editing.postDetailVisibility.classificationAxes !== false,
      assessment: editing.postDetailVisibility.assessment !== false,
      aggregateScores: editing.postDetailVisibility.aggregateScores !== false,
      fieldsTable: editing.postDetailVisibility.fieldsTable !== false,
    };
    schema.postDetailVisibility = pv;
    // docs/27 §17-4: 机上調査の設定はこの画面では編集しない。以前の設定は消さずにそのまま書き戻す。
    if (Object.keys(editing.streetSurvey).length > 0) schema.streetSurvey = editing.streetSurvey;

    setSaving(true);
    try {
      await upsertMaster<AiOutputFormat>({
        collectionName: COLLECTION_NAME,
        id: editing.id.trim() || undefined,
        uid: 'admin-stub', // Phase 5 後続で実 master role uid に置換
        isNew,
        data: {
          mode: editing.mode,
          displayName: editing.displayName.trim(),
          schema,
          enabled: true,
          version: 1,
          schemaVersion: 1,
          archivedAt: null,
        },
      });
      toast('success', isNew ? '新規作成しました' : '更新しました');
      handleCancel(); // 編集面を閉じ、複製状態 / ID の手入力状態もリセットする
    } catch (e) {
      toast('error', `保存失敗: ${(e as Error).message}`);
    } finally {
      setSaving(false);
    }
  }

  async function handleArchive(id: string, name: string) {
    if (!confirm(`「${name}」をアーカイブしますか? (物理削除ではなく archivedAt 設定)`)) return;
    try {
      await archiveMaster(COLLECTION_NAME, id, 'admin-stub');
      toast('success', 'アーカイブしました');
    } catch (e) {
      toast('error', `アーカイブ失敗: ${(e as Error).message}`);
    }
  }

  return (
    <div>
      <header className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900">AI 出力フォーマットマスター</h1>
        <p className="text-sm text-slate-500 mt-1">
          AI 出力スキーマ (内部 ID: aiOutputFormats、docs/04 §3-1 M3)。Sprint M3 で
          SimpleJsonMasterPage から専用フォームに置換 (R-32 step 2 完了に伴う構造化編集化)。
        </p>
      </header>

      <div className="mb-4 flex flex-wrap gap-2 items-center">
        <Button variant="primary" size="md" onClick={handleNew} disabled={editing !== null}>
          ＋ 新規作成
        </Button>
        {/* AI解析統合エディタと同じ理由 (2026-07-14) — このページも組込モードに加え、地区が
            ModeProfile 経由で登録した custom モード (`DistrictMode = BuiltInDistrictMode |
            (string & {})`) の出力フォーマットを横断して扱う。expectedMode を組込4モードに
            固定すると custom モードのフォーマットが軒並み skip されてしまうため、mode 値の
            制限は行わない (validateAiOutputFormat の構造チェックのみに委ねる)。 */}
        <MasterImportExportButtons<AiOutputFormat & { id: string }>
          collectionName={COLLECTION_NAME}
          items={activeItems}
          validator={validateAiOutputFormat}
          onImport={handleImport}
          disabled={editing !== null}
        />
      </div>

      {state.status === 'loading' && <p className="text-slate-500">読込中…</p>}
      {state.status === 'error' && (
        <p className="text-danger-600">読込エラー: {state.error.message}</p>
      )}
      {state.status === 'ready' && (
        <>
          <p className="text-xs text-slate-500 mb-3">
            件数: {activeItems.length} (アーカイブ済除外)
            {archivedCount > 0 && (
              <span className="ml-2">
                ／ アーカイブ済 {archivedCount} 件（ドキュメント ID の重複判定に含めます）
              </span>
            )}
          </p>
          <ul className="grid grid-cols-1 gap-2 mb-6">
            {activeItems.map((item) => {
              const usage = usageByFormat[item.id];
              const unused = usageReady && !usage;
              return (
                <li key={item.id}>
                  <Card padding="md" className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-[260px] flex-1">
                      <div>
                        <strong className="text-slate-900">{item.displayName}</strong>
                        <span className="ml-2 text-xs text-slate-500">id: {item.id}</span>
                        <span className="ml-2 text-xs text-slate-500">mode: {item.mode}</span>
                        <span className="ml-2 text-xs text-slate-500">
                          axes: {item.schema?.classificationAxes?.length ?? 0}
                        </span>
                        {unused && (
                          <span
                            className="ml-2 inline-block px-1.5 py-0.5 text-[11px] bg-amber-50 border border-amber-200 text-amber-800 rounded"
                            title="どの AI 解析テンプレートからも、どの地区からも参照されていません。アーカイブしても解析への影響はありません。"
                          >
                            どこからも未使用
                          </span>
                        )}
                      </div>
                      <FormatUsageSummary usage={usage} ready={usageReady} />
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      <RowExportButton collectionName={COLLECTION_NAME} item={item} />
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleEdit(item)}
                        aria-label={`${item.displayName} を編集`}
                      >
                        編集
                      </Button>
                      {/* アーカイブ済は一覧に出さないため、複製は常に有効な行だけに出る。 */}
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleDuplicateFromRow(item)}
                        aria-label={`${item.displayName} を複製`}
                        title="この出力フォーマットの内容を引き継いだ新しいフォーマットの下書きを作ります（この操作では保存されません）"
                      >
                        📋 複製
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setHistoryTarget({ id: item.id, name: item.displayName })}
                        aria-label={`${item.displayName} の履歴を表示`}
                      >
                        履歴
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => void handleArchive(item.id, item.displayName)}
                        aria-label={`${item.displayName} をアーカイブ`}
                      >
                        アーカイブ
                      </Button>
                    </div>
                  </Card>
                </li>
              );
            })}
          </ul>
        </>
      )}

      {historyTarget && (
        <MasterHistoryView
          collectionName={COLLECTION_NAME}
          masterId={historyTarget.id}
          masterDisplayName={historyTarget.name}
          onClose={() => setHistoryTarget(null)}
        />
      )}

      {editing && (
        <Card padding="lg" className="border-primary-300">
          <h2 className="text-lg font-bold mb-3">{isNew ? '新規作成' : '編集'}</h2>
          {duplicatedFrom != null && (
            <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
              <p className="font-bold">
                「{duplicatedFrom}」を複製した、まだ保存していない出力フォーマットです。
              </p>
              <p className="mt-1">
                表示名称とドキュメント ID
                を確認して「保存」を押すと、新しい出力フォーマットとして作成されます。ここでの編集は
                複製元には影響しません。
              </p>
            </div>
          )}
          <div className="grid grid-cols-1 gap-3">
            {/* 1. 基本情報 */}
            <div>
              <label className="block text-xs text-slate-600 mb-1" htmlFor="m3-name">
                表示名称 (displayName) <span className="text-danger-600">*</span>
              </label>
              <input
                id="m3-name"
                type="text"
                value={editing.displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                className={FIELD_CLASS}
              />
            </div>
            <div>
              <label className="block text-xs text-slate-600 mb-1" htmlFor="m3-id">
                ドキュメント ID (id){' '}
                {isNew ? (
                  <span className="text-danger-600">*</span>
                ) : (
                  <span className="text-slate-400">(変更できません)</span>
                )}
              </label>
              <div className="flex gap-2 items-start">
                <input
                  id="m3-id"
                  type="text"
                  value={editing.id}
                  onChange={(e) => setDocumentId(e.target.value)}
                  onBlur={() => setIdTouched(true)}
                  readOnly={!isNew}
                  aria-invalid={visibleIdError ? true : undefined}
                  className={`${FIELD_CLASS} flex-1 font-mono ${
                    !isNew ? 'bg-slate-100 text-slate-600' : ''
                  }`}
                  placeholder="例: ecw-default-output"
                  spellCheck={false}
                />
                {isNew && idManuallyEdited && (
                  <Button variant="secondary" size="sm" onClick={regenerateDocumentId}>
                    表示名から自動生成
                  </Button>
                )}
              </div>
              {isNew ? (
                <>
                  {visibleIdError && (
                    <p className="mt-1 text-[11px] text-danger-600">{visibleIdError}</p>
                  )}
                  <p className="mt-1 text-[11px] text-slate-500 leading-relaxed">
                    半角の英小文字・数字・ハイフン (-) が使えます (例:
                    ecw-default-output)。表示名称から自動で作られるので、決めたい ID
                    があるときだけ書き換えてください。
                    <span className="font-bold">保存すると後から変更できません</span>
                    （AI 解析テンプレートと地区はこの ID で出力フォーマットを参照します）。
                  </p>
                </>
              ) : (
                <p className="mt-1 text-[11px] text-slate-500 leading-relaxed">
                  AI 解析テンプレートと地区が出力フォーマットを参照するための ID
                  です。作成後は変更できません。別の ID
                  にしたいときは「別名で保存」で新しい出力フォーマットを作り、そちらに ID
                  を付けてください。
                </p>
              )}
            </div>
            <div>
              <label className="block text-xs text-slate-600 mb-1" htmlFor="m3-mode">
                モード (mode)
              </label>
              {/* MD-3 モードドリブン化: 旧「組込4種ハードコード + custom fallback (2026-07-14)」を
                  ModeProfile (M10) 登記簿駆動の共通コンポーネントに置換 (未登録の現在値も表示)。 */}
              <ModeSelectField
                id="m3-mode"
                value={editing.mode}
                onChange={(mode) => setEditing({ ...editing, mode })}
                className={FIELD_CLASS}
              />
            </div>
            <div>
              <label className="block text-xs text-slate-600 mb-1" htmlFor="m3-rootkey">
                ルートキー (rootKey、任意)
              </label>
              <input
                id="m3-rootkey"
                type="text"
                value={editing.rootKey}
                onChange={(e) => setEditing({ ...editing, rootKey: e.target.value })}
                className={FIELD_CLASS}
                placeholder="例: ecw_w / result (空欄ならトップレベル直書き)"
              />
              <p className="text-[10px] text-slate-500 mt-1">
                AI 出力 JSON 全体を <code>{'{ <rootKey>: { ... } }'}</code> でネストする場合の
                外側キー名。ウォーカブル mode は <code>ecw_w</code> でネストする例あり。 未指定なら
                AI 出力 JSON のトップレベルから直接読み取ります。
              </p>
            </div>

            {/* 2. classificationAxes */}
            <div className="border-t border-slate-200 pt-3 mt-2">
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-bold text-slate-700">
                  分類軸 (classificationAxes、{editing.classificationAxes.length} 件、最低 1 件 /
                  primary 1 件)
                  <span className="text-danger-600"> *</span>
                </label>
                <Button variant="secondary" size="sm" onClick={addAxis}>
                  ＋ 軸を追加
                </Button>
              </div>
              <p className="text-xs text-slate-500 mb-2">
                AI 出力 <code>classifications</code> 配列の各要素が持つ「コード／表示名のペア」を
                定義します。1 つの分類要素に複数の独立した分類体系を持たせたい場合 (例:
                防災=カテゴリ + ハザード種別 + 危険度) は軸を複数追加してください。各列の意味:
                <span className="block mt-1">
                  <strong>codeField</strong> = AI 出力 JSON のコードフィールド名 (例:{' '}
                  <code>category_code</code>) /<strong> nameField</strong> = その表示名フィールド名
                  (例: <code>category_name</code>) /<strong> subField</strong> =
                  連鎖コードを保持する任意フィールド (例: <code>sub_hzr</code>) /
                  <strong> label</strong> = admin/UI 表示用の日本語ラベル (例:「カテゴリ」) /
                  <strong> role</strong> = <code>primary</code> (主軸、必須 1 件、validators が
                  この軸の code 空要素を除外) / <code>secondary</code> (副軸) / (未指定)
                </span>
              </p>
              {editing.classificationAxes.length === 0 ? (
                <p className="text-xs text-slate-500">
                  軸がありません。「＋ 軸を追加」で追加してください。
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs border-collapse">
                    <thead>
                      <tr className="text-left text-slate-600 border-b border-slate-200">
                        <th className="py-1 pr-2 font-medium">
                          コード (codeField) <span className="text-danger-600">*</span>
                        </th>
                        <th className="py-1 pr-2 font-medium">
                          表示名 (nameField) <span className="text-danger-600">*</span>
                        </th>
                        <th className="py-1 pr-2 font-medium">サブ (subField)</th>
                        <th className="py-1 pr-2 font-medium">
                          ラベル (label) <span className="text-danger-600">*</span>
                        </th>
                        <th className="py-1 pr-2 font-medium">役割 (role)</th>
                        <th className="py-1 w-12"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {editing.classificationAxes.map((axis, i) => (
                        <tr key={i} className="border-b border-slate-100">
                          <td className="py-1 pr-2">
                            <input
                              type="text"
                              value={axis.codeField}
                              onChange={(e) => updateAxis(i, { codeField: e.target.value })}
                              className={INPUT_CLASS}
                              placeholder="category_code"
                              aria-label={`軸 ${i + 1} コードフィールド名 (codeField)`}
                            />
                          </td>
                          <td className="py-1 pr-2">
                            <input
                              type="text"
                              value={axis.nameField}
                              onChange={(e) => updateAxis(i, { nameField: e.target.value })}
                              className={INPUT_CLASS}
                              placeholder="category_name"
                              aria-label={`軸 ${i + 1} 表示名フィールド名 (nameField)`}
                            />
                          </td>
                          <td className="py-1 pr-2">
                            <input
                              type="text"
                              value={axis.subField}
                              onChange={(e) => updateAxis(i, { subField: e.target.value })}
                              className={INPUT_CLASS}
                              placeholder="(任意) sub_code"
                              aria-label={`軸 ${i + 1} サブフィールド名 (subField)`}
                            />
                          </td>
                          <td className="py-1 pr-2">
                            <input
                              type="text"
                              value={axis.label}
                              onChange={(e) => updateAxis(i, { label: e.target.value })}
                              className={INPUT_CLASS}
                              placeholder="カテゴリ"
                              aria-label={`軸 ${i + 1} 表示ラベル (label)`}
                            />
                          </td>
                          <td className="py-1 pr-2">
                            <select
                              value={axis.role}
                              onChange={(e) =>
                                updateAxis(i, { role: e.target.value as AxisDraft['role'] })
                              }
                              className={INPUT_CLASS}
                              aria-label={`軸 ${i + 1} 役割 (role)`}
                            >
                              <option value="">(未指定)</option>
                              <option value="primary">primary</option>
                              <option value="secondary">secondary</option>
                            </select>
                          </td>
                          <td className="py-1 text-right">
                            <Button
                              variant="danger"
                              size="sm"
                              onClick={() => removeAxis(i)}
                              aria-label={`分類軸 ${i + 1} 削除`}
                            >
                              ✕
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* 3. assessment */}
            <div className="border-t border-slate-200 pt-3">
              <h3 className="text-sm font-bold text-slate-700 mb-1">評価ラベル (assessment)</h3>
              <p className="text-xs text-slate-500 mb-3">
                AI が各 classifications につける「印象 (肯定 / 否定)」と「強度ラベル」を定義します。
                v2 functions は AI 出力の <code>assessment</code> フィールド値が肯定ラベル /
                否定ラベルのどちらと一致するかで集計対象を仕分けます (例: ECW=喜び、 防災=ホッと /
                ハッと、ウォーカブル=いいね！/ もったいない)。
              </p>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div>
                  <label className="block text-xs text-slate-600 mb-1" htmlFor="m3-poslabel">
                    肯定ラベル (positiveLabel)
                  </label>
                  <input
                    id="m3-poslabel"
                    type="text"
                    value={editing.assessmentPositiveLabel}
                    onChange={(e) =>
                      setEditing({ ...editing, assessmentPositiveLabel: e.target.value })
                    }
                    className={FIELD_CLASS}
                    placeholder="例: いいね！/ ホッと / 喜び"
                  />
                  <p className="text-[10px] text-slate-500 mt-1">
                    AI が「良い印象」と判断した分類につく文字列。AI 出力の <code>assessment</code>{' '}
                    値がこの文字列と一致した classifications は 肯定側集計スコア
                    (aggregateScores.positiveKey) に加算されます。
                  </p>
                </div>
                <div>
                  <label className="block text-xs text-slate-600 mb-1" htmlFor="m3-neglabel">
                    否定ラベル (negativeLabel)
                  </label>
                  <input
                    id="m3-neglabel"
                    type="text"
                    value={editing.assessmentNegativeLabel}
                    onChange={(e) =>
                      setEditing({ ...editing, assessmentNegativeLabel: e.target.value })
                    }
                    className={FIELD_CLASS}
                    placeholder="例: もったいない / ハッと"
                  />
                  <p className="text-[10px] text-slate-500 mt-1">
                    AI が「悪い印象」と判断した分類につく文字列。否定側集計スコア
                    (aggregateScores.negativeKey) に加算されます。
                  </p>
                </div>
              </div>
              <div className="mt-3">
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-xs text-slate-600">
                    評価レベル (levels、{editing.assessmentLevels.length} 件、最低 1 件)
                    <span className="text-danger-600"> *</span>
                  </label>
                  <Button variant="secondary" size="sm" onClick={addLevel}>
                    ＋ level 追加
                  </Button>
                </div>
                <p className="text-[10px] text-slate-500 mb-2">
                  AI 出力 <code>assessment_level</code> が取り得る強度ラベルの配列。 classifications
                  内の各要素はこのどれかの値を持つ前提で UI 表示・色分けされます。 代表例:「弱 / 中
                  / 強」「小 / 中 / 大」など。
                </p>
                {editing.assessmentLevels.length === 0 ? (
                  <p className="text-xs text-slate-500">
                    level がありません。「＋ level 追加」で追加してください (例: 弱 / 中 / 強)。
                  </p>
                ) : (
                  <div className="grid grid-cols-1 gap-1">
                    {editing.assessmentLevels.map((level, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <input
                          type="text"
                          value={level}
                          onChange={(e) => updateLevel(i, e.target.value)}
                          className={`${INPUT_CLASS} flex-1`}
                          placeholder={`level ${i + 1}`}
                          aria-label={`レベル ${i + 1} (level)`}
                        />
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => removeLevel(i)}
                          aria-label={`レベル ${i + 1} 削除`}
                        >
                          ✕
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* 4. aggregateScores */}
            <div className="border-t border-slate-200 pt-3">
              <h3 className="text-sm font-bold text-slate-700 mb-1">
                集約スコアキー (aggregateScores、全て任意)
              </h3>
              <p className="text-xs text-slate-500 mb-3">
                classifications を肯定ラベル / 否定ラベルで仕分けし、合計したスコアを
                <strong> 出力 JSON のどのキー名に書き戻すか</strong> を指定します。AI ではなく v2
                functions が分析後に集計して書き込みます (sum / max / avg を含むオブジェクト)。 key
                名を指定しない場合、そのスコアは出力に含まれません。
              </p>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                <div>
                  <label className="block text-xs text-slate-600 mb-1" htmlFor="m3-aggpos">
                    肯定スコアキー (positiveKey)
                  </label>
                  <input
                    id="m3-aggpos"
                    type="text"
                    value={editing.aggregatePositiveKey}
                    onChange={(e) =>
                      setEditing({ ...editing, aggregatePositiveKey: e.target.value })
                    }
                    className={FIELD_CLASS}
                    placeholder="例: iine_score / hotto_score"
                  />
                  <p className="text-[10px] text-slate-500 mt-1">
                    肯定ラベルの classifications の score 合計を入れる出力フィールド名。
                  </p>
                </div>
                <div>
                  <label className="block text-xs text-slate-600 mb-1" htmlFor="m3-aggneg">
                    否定スコアキー (negativeKey)
                  </label>
                  <input
                    id="m3-aggneg"
                    type="text"
                    value={editing.aggregateNegativeKey}
                    onChange={(e) =>
                      setEditing({ ...editing, aggregateNegativeKey: e.target.value })
                    }
                    className={FIELD_CLASS}
                    placeholder="例: mottainai_score / hatto_score"
                  />
                  <p className="text-[10px] text-slate-500 mt-1">
                    否定ラベルの classifications の score 合計を入れる出力フィールド名。
                  </p>
                </div>
                <div>
                  <label className="block text-xs text-slate-600 mb-1" htmlFor="m3-aggover">
                    総合キー (overallKey)
                  </label>
                  <input
                    id="m3-aggover"
                    type="text"
                    value={editing.aggregateOverallKey}
                    onChange={(e) =>
                      setEditing({ ...editing, aggregateOverallKey: e.target.value })
                    }
                    className={FIELD_CLASS}
                    placeholder="例: walkable_score"
                  />
                  <p className="text-[10px] text-slate-500 mt-1">
                    肯定 - 否定 などで計算する総合スコアを入れる出力フィールド名 (mode 別に 計算式は
                    v2 functions 側で定義)。
                  </p>
                </div>
              </div>
            </div>

            {/* 5. reasoningField */}
            <div className="border-t border-slate-200 pt-3">
              <label className="block text-xs text-slate-600 mb-1" htmlFor="m3-reasoning">
                根拠フィールド名 (reasoningField、classifications 内)
                <span className="text-danger-600"> *</span>
              </label>
              <input
                id="m3-reasoning"
                type="text"
                value={editing.reasoningField}
                onChange={(e) => setEditing({ ...editing, reasoningField: e.target.value })}
                className={FIELD_CLASS}
                placeholder="例: reasoning / comment"
              />
              <p className="text-[10px] text-slate-500 mt-1">
                AI 出力 <code>classifications[]</code> の各要素に置く「分類根拠コメント」の
                フィールド名。validators が読み込んで UI に表示する用途。投稿カード・処方箋・ CSV
                出力でこのフィールドが「コメント」「根拠」として表示されます。
              </p>
            </div>

            {/* issue #24: 投稿詳細での表示 (テンプレ既定) */}
            <div className="border-t border-slate-200 pt-3">
              <div className="text-xs font-medium text-slate-700 mb-1">
                投稿詳細での表示（テンプレ既定）
              </div>
              <p className="text-[10px] text-slate-500 mb-2">
                投稿アプリの投稿詳細で、この出力フォーマットの各要素を表示するかの既定値です。
                チェックを外すと非表示になります。地区ごとに上書きできます（地区管理
                →「分析表示設定」）。
              </p>
              <div className="grid grid-cols-1 gap-1.5">
                {POST_DETAIL_VISIBILITY_KEYS.map((k) => (
                  <label key={k} className="flex items-start gap-2 text-xs text-slate-700">
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      checked={editing.postDetailVisibility[k] !== false}
                      onChange={(e) =>
                        setEditing({
                          ...editing,
                          postDetailVisibility: {
                            ...editing.postDetailVisibility,
                            [k]: e.target.checked,
                          },
                        })
                      }
                    />
                    <span>{POST_DETAIL_VISIBILITY_LABELS[k]}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* 机上調査の設定は「机上調査テンプレート」で行う (docs/27 §17-4、2026-09-16 B7 で編集欄を撤去)。
                以前この画面で保存した設定 (schema.streetSurvey) は消さずに保存し直し、テンプレートより下の層として使われる。 */}
            <div className="border-t border-slate-200 pt-3">
              <p className="text-xs font-semibold text-slate-700 mb-1">
                ストリートビュー机上調査の設定
              </p>
              <p className="text-xs text-slate-600">
                机上調査の調査項目の既定・解析の設定と文面は、マスタ「
                <a
                  href="/masters/street-survey-templates"
                  className="text-primary-600 hover:underline"
                >
                  机上調査テンプレート
                </a>
                」で設定し、地区の机上調査ページで適用・上書きします。
              </p>
              {Object.keys(editing.streetSurvey).length > 0 && (
                <p className="mt-1 text-[11px] text-slate-500">
                  この出力フォーマットには、以前この画面で保存した机上調査の設定が残っています。保存しても消えず、机上調査テンプレートより下の層（テンプレートにも地区にも無い項目の既定）として使われます。
                </p>
              )}
            </div>
            {/* 6. customSchemaText */}
            <div className="border-t border-slate-200 pt-3">
              <label className="block text-xs text-slate-600 mb-1" htmlFor="m3-customschema">
                カスタムスキーマ (customSchemaText、任意)
              </label>
              <textarea
                id="m3-customschema"
                value={editing.customSchemaText}
                onChange={(e) => setEditing({ ...editing, customSchemaText: e.target.value })}
                rows={6}
                className="w-full px-3 py-2 text-xs font-mono border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-400 resize-y"
                placeholder="(任意) JSON Schema を自由記述、空欄なら上の構造から自動生成"
                spellCheck={false}
              />
              <p className="text-xs text-slate-500 mt-1">
                通常は空欄推奨。値が設定されると、上の classificationAxes / assessment /
                aggregateScores から自動生成されるスキーマを完全に上書きします。AI に厳密な JSON
                Schema を渡したい高度な用途専用。
              </p>
            </div>

            {/* 7. fields[] — 列名動的化レベル 3 (docs/105) */}
            <div className="border-t border-slate-200 pt-3">
              <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
                <h3 className="text-sm font-bold text-slate-700">
                  出力フィールド (fields[]、{editing.fields.length} 件) — 列名動的化
                </h3>
                <div className="flex items-center gap-2 flex-wrap">
                  <select
                    value={newFieldKind}
                    onChange={(e) => setNewFieldKind(e.target.value as OutputFieldKind)}
                    className={INPUT_CLASS}
                    style={{ width: 'auto', maxWidth: '100%' }}
                    aria-label="追加するフィールド種別 (kind)"
                  >
                    <option value="text">text (単一文字列)</option>
                    <option value="enum-array">enum-array (列挙配列)</option>
                    <option value="code-list-string">code-list-string (コード列)</option>
                    <option value="object-array">object-array (オブジェクト配列)</option>
                  </select>
                  <Button variant="secondary" size="sm" onClick={addField}>
                    ＋ フィールドを追加
                  </Button>
                  <Button variant="secondary" size="sm" onClick={generateFieldsFromStructured}>
                    構造化設定から生成
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={clearFields}
                    disabled={editing.fields.length === 0}
                  >
                    fields[] をクリア
                  </Button>
                </div>
              </div>
              <p className="text-xs text-slate-500 mb-2">
                AI 出力 JSON の「列 (キー名)」そのものをデータとして定義します (docs/105、
                動的化レベル 3)。<code>kind</code> の組み合わせで任意の出力構造を表現でき、
                解析時の列名の唯一の真実になります。空のままなら従来通り classificationAxes /
                assessment から自動生成されます。
              </p>
              {editing.fields.length > 0 && (
                <p className="text-xs text-primary-700 bg-primary-50 border border-primary-200 rounded px-2 py-1 mb-2">
                  fields[] が設定されているため、解析時はこちらが classificationAxes より
                  優先されます (列名の唯一の真実)。
                </p>
              )}
              {editing.fields.length === 0 ? (
                <p className="text-xs text-slate-500">
                  fields[] がありません。kind を選んで「＋ フィールドを追加」するか、
                  「構造化設定から生成」で既存設定を起点に編集してください。
                </p>
              ) : (
                <div className="grid grid-cols-1 gap-3">
                  {editing.fields.map((field, i) => (
                    <FieldEditor
                      key={i}
                      index={i}
                      total={editing.fields.length}
                      field={field}
                      onChange={(next) => updateField(i, next)}
                      onRemove={() => removeField(i)}
                      onMove={(dir) => moveField(i, dir)}
                      onAddChild={(kind) => addChildField(i, kind)}
                      onUpdateChild={(ci, child) => updateChildField(i, ci, child)}
                      onRemoveChild={(ci) => removeChildField(i, ci)}
                    />
                  ))}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 mt-2 border-t border-slate-200 pt-3">
              <Button variant="secondary" size="md" onClick={handleCancel} disabled={saving}>
                キャンセル
              </Button>
              {!isNew && (
                <Button
                  variant="secondary"
                  size="md"
                  onClick={handleDuplicateCurrent}
                  disabled={saving}
                  title="いま編集している内容を引き継いで、新しい出力フォーマットの下書きを作ります（この操作では保存されません）"
                >
                  別名で保存
                </Button>
              )}
              <Button
                variant="primary"
                size="md"
                onClick={() => void handleSave()}
                disabled={saving || visibleIdError != null}
                title={
                  visibleIdError != null
                    ? 'ドキュメント ID を修正すると保存できます'
                    : '編集内容を保存します'
                }
              >
                {saving ? '保存中...' : '保存'}
              </Button>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}

/**
 * 一覧行に出す「使用テンプレート / 使用地区」。
 *
 * 出力フォーマット (M3) は 2 つの経路から参照される (docs/105 A-③):
 *   1. AI 解析テンプレート (M1) の `outputFormatId`
 *   2. 地区設定の直接指定 (`aiAnalysis.outputFormatIdOverride`、テンプレートの設定より優先)
 *
 * どちらの経路で使われているか (= 編集・アーカイブの影響範囲) が分かるよう、地区バッジには
 * 直接指定の注記を添える。バッジの見た目は AI 解析テンプレート一覧と共用 (UsageBadges)。
 */
function FormatUsageSummary(props: { usage: OutputFormatUsage | undefined; ready: boolean }) {
  const { usage, ready } = props;

  const templateBadges: UsageBadgeItem[] = (usage?.templates ?? []).map((t) => ({
    key: t.id,
    label: `${t.displayName} (${t.id})`,
    note: t.archived ? 'アーカイブ済み' : undefined,
    tone: t.archived ? 'muted' : 'template',
    title: t.archived
      ? 'アーカイブ済みの AI 解析テンプレートが、この出力フォーマットを参照しています'
      : 'この AI 解析テンプレートが、出力フォーマットとして参照しています',
  }));

  const districtBadges: UsageBadgeItem[] = (usage?.districts ?? []).map((d) => ({
    key: `${d.via}:${d.id}`,
    label: d.name,
    note: d.via === 'direct' ? '直接指定' : undefined,
    title:
      d.via === 'direct'
        ? '地区の設定でこの出力フォーマットを直接指定しています（AI 解析テンプレートの設定より優先されます）'
        : `AI 解析テンプレート「${d.templateName ?? ''}」経由で使用しています`,
  }));

  return (
    <dl className="mt-2 grid grid-cols-1 gap-1">
      <div className="flex gap-2 items-start">
        <dt className="w-[7rem] shrink-0 text-xs text-slate-500">使用テンプレート</dt>
        <dd className="flex-1">
          <UsageBadges
            items={templateBadges}
            emptyLabel={ready ? 'どの AI 解析テンプレートからも使われていません' : '読込中…'}
          />
        </dd>
      </div>
      <div className="flex gap-2 items-start">
        <dt className="w-[7rem] shrink-0 text-xs text-slate-500">使用地区</dt>
        <dd className="flex-1">
          <UsageBadges
            items={districtBadges}
            emptyLabel={ready ? '使用中の地区はありません' : '読込中…'}
          />
        </dd>
      </div>
    </dl>
  );
}
